import { SERVICE_NAME } from "config";

export const setItem = (key, value) => {
  localStorage.setItem(key, JSON.stringify(value));
};

export const getItem = (key) => {
  const item = localStorage.getItem(key);
  return item ? JSON.parse(item) : null;
};

export const setIDToken = (token) => {
  setItem(`${SERVICE_NAME}_ID_TOKEN`, token);
};

export const getIDToken = () => {
  const token = getItem(`${SERVICE_NAME}_ID_TOKEN`);
  return token;
};

export const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const setActiveUser = (user) => {
  setItem(`${SERVICE_NAME}_ACTIVE_USER`, user);
};

export const getActiveUser = () => {
  const user = getItem(`${SERVICE_NAME}_ACTIVE_USER`);
  return user;
};

export const debounce = (func, delay) => {
  let timer;

  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
};

export const flattenObject = (obj, prefix = "", result = {}, skipkeys = []) => {
  for (let key in obj) {
    if (skipkeys.includes(key)) {
      console.log("!!! SKIPPING KEY: ", key);
      continue;
    }
    // Check if the property is an object or an array
    if (typeof obj[key] === "object" && obj[key] !== null) {
      // If it's an array, differentiate between an array of primitives and an array of objects
      if (Array.isArray(obj[key]) && typeof obj[key] !== "string") {
        obj[key].forEach((item, index) => {
          if (typeof item === "object" && item !== null) {
            // Recursive call for array of objects
            flattenObject(item, `${prefix}${key}:${index}:`, result, skipkeys);
          } else {
            // Directly assign for array of primitives
            result[`${prefix}${key}:${index}`] = item;
          }
        });
      } else {
        // Recursive call for nested objects
        flattenObject(obj[key], `${prefix}${key}:`, result, skipkeys);
      }
    } else {
      // Base case: it's a primitive value, so set the key in the result
      result[`${prefix}${key}`] = obj[key];
    }
  }
  return result;
};

export const unflattenObject = (flatObject) => {
  const result = {};

  for (const key in flatObject) {
    const parts = key.split(":");
    let current = result;

    for (let i = 0; i < parts.length; i++) {
      // Determine if we're at the last part of the key
      if (i === parts.length - 1) {
        current[parts[i]] = flatObject[key];
      } else {
        // Check if the next part of the key is a number (indicating an array)
        const isArray = !isNaN(parseInt(parts[i + 1], 10));
        if (!current[parts[i]]) {
          current[parts[i]] = isArray ? [] : {};
        }
        current = current[parts[i]];
      }
    }
  }

  return result;
};

export const toFixed = (value, precision = 2) => {
  return parseFloat(value).toFixed(precision);
};

export const getLocalDateFromString = (dateString) => {
  const date = new Date(dateString);
  const userTimezoneOffset = date.getTimezoneOffset() * 60000;
  const localDate = new Date(date.getTime() - userTimezoneOffset);
  return localDate;
};

export const dateTimeFormatter = new Intl.DateTimeFormat("default", {
  year: "numeric",
  month: "long",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
  timeZoneName: "short",
});

export const numericDateTimeFormatter = new Intl.DateTimeFormat("default", {
  year: "numeric",
  month: "numeric",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
  second: "numeric",
  timeZoneName: "short",
});

export const getDuration = (end, start) => {
  // User friendly duration hours, minutes, seconds
  const startDate = new Date(start);
  const endDate = new Date(end);
  const duration = endDate - startDate;
  const seconds = Math.floor((duration / 1000) % 60);
  const minutes = Math.floor((duration / (1000 * 60)) % 60);
  const hours = Math.floor((duration / (1000 * 60 * 60)) % 24);

  return `${hours}h ${minutes}m ${seconds}s`;
};

export const formatSeconds = (seconds) => {
  seconds = Math.round(seconds);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  return `${hours}h ${minutes}m ${remainingSeconds}s`;
};

export const getAbbreviatedTimeZone = () => {
  const date = new Date();
  const options = { timeZoneName: "short" };
  const formatter = new Intl.DateTimeFormat("en-US", options);
  const parts = formatter.formatToParts(date);
  const timeZonePart = parts.find((part) => part.type === "timeZoneName");
  return timeZonePart?.value || "UTC";
};

export const getFullTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export const fromDateToString = (date) => {
  date = new Date(+date);
  date.setTime(date.getTime() - date.getTimezoneOffset() * 60000);
  let dateAsString = date.toISOString().substr(0, 16);
  return dateAsString;
};

export const watchHlsSimple = (
  mediaPlaylistUrl,
  onStatus = () => {},
  { intervalMs = 5000, stableForMs = 30000, cacheBust = true }
) => {
  console.log("!!! WATCH MEDIA PLAYLIST URL: ", mediaPlaylistUrl);
  console.log("!!! WATCH INTERVAL MS: ", intervalMs);
  console.log("!!! WATCH STABLE FOR MS: ", stableForMs);
  console.log("!!! WATCH CACHE BUST: ", cacheBust);
  let stopped = false;
  let timer = null;

  let lastLine = null; // last non-# line we saw
  let lastChangeAt = Date.now();

  const tick = async () => {
    if (stopped) return;
    try {
      const url = cacheBust
        ? mediaPlaylistUrl +
          (mediaPlaylistUrl.includes("?") ? "&" : "?") +
          "cb=" +
          Date.now()
        : mediaPlaylistUrl;

      console.log("!!! GETTING HLS PLAYLIST: ", url);

      const res = await fetch(url, { cache: "no-store" });
      if (!res.ok) {
        console.log("!!! HLS FETCH ERROR: ", res.status, res.statusText);
      }
      const text = await res.text();

      // 1) Definitive end, file is gone
      if (/#EXT-X-ENDLIST/.test(text) || !/#EXTM3U/.test(text)) {
        onStatus({
          definiteEnded: true,
          probablyEnded: true,
          reason: "ENDLIST",
        });
        stop();
        return;
      }

      // 2) Heuristic: last non-tag line (segment URI)
      const lines = text.trim().split(/\r?\n/);
      const lastNonTag =
        [...lines].reverse().find((l) => l && !l.startsWith("#")) || null;

      if (lastNonTag && lastNonTag !== lastLine) {
        lastLine = lastNonTag;
        lastChangeAt = Date.now();
        onStatus({
          definiteEnded: false,
          probablyEnded: false,
          last: lastLine,
          reason: "New segment listed",
        });
      } else {
        const idle = Date.now() - lastChangeAt;
        onStatus({
          definiteEnded: false,
          probablyEnded: idle >= stableForMs,
          last: lastLine,
          idleMs: idle,
          thresholdMs: stableForMs,
          reason: `Unchanged for ${Math.round(idle / 1000)}s`,
        });
      }
    } catch (e) {
      const errorMsg = e instanceof Error ? e.message : String(e);
      onStatus({ error: errorMsg });
    } finally {
      if (!stopped) timer = setTimeout(tick, intervalMs);
    }
  };

  const stop = () => {
    stopped = true;
    if (timer) clearTimeout(timer);
  };

  tick();
  return stop; // call this to stop polling
};
